import { AffiliationPassive, CardElement } from "../types/battle"

export const AFFILIATION_ELEMENTS: CardElement[] = [
  "FIRE",
  "WATER",
  "EARTH",
  "LIGHTNING",
  "WIND",
  "ICE",
]

export const AFFILIATION_ELEMENT_TITLES: Record<CardElement, string> = {
  FIRE: "Огонь",
  WATER: "Вода",
  EARTH: "Земля",
  LIGHTNING: "Молния",
  WIND: "Ветер",
  ICE: "Лёд",
}

/**
 * Названия пассивов (GUARDIAN_PASSIVES) — fallback, если бэк не отдал title.
 * Актуальный источник: GET /game/mechanics → affiliationElements и passport.affiliationPassive.
 */
export const AFFILIATION_ELEMENT_PASSIVE_TITLES: Partial<
  Record<CardElement, string>
> = {
  WIND: "Шквал",
  ICE: "Морозное касание",
}

/** Одна строка пассива (early-фаза) — fallback; приоритет у API */
export const AFFILIATION_ELEMENT_PASSIVE_LINES: Record<CardElement, string> = {
  FIRE: "+14% силы с картой огня",
  WATER: "+7% силы; −25% потери рейтинга при поражении",
  EARTH: "+13% силы на защитных картах",
  LIGHTNING: "+4% силы; шанс крита +18%",
  WIND: "+5% силы (+10% на SPEED); 12% спасти поражение → ничья",
  ICE: "+7% силы; враг −3% силы; контр-пик стихии врага слабее на 50%",
}

export type AffiliationDifficulty = "easy" | "medium" | "hard"

export interface AffiliationElementGuide {
  /** Короткая строка на карточке выбора */
  summary: string
  /** Пояснение, как работает пассив */
  detail: string
  pros: readonly string[]
  cons: readonly string[]
  difficulty: AffiliationDifficulty
}

export const AFFILIATION_DIFFICULTY_LABELS: Record<AffiliationDifficulty, string> = {
  easy: "Легко",
  medium: "Средне",
  hard: "Сложно",
}

/** Подробные тексты для выбора стихии и модалки в профиле (fallback без API) */
export const AFFILIATION_ELEMENT_GUIDES: Record<CardElement, AffiliationElementGuide> = {
  FIRE: {
    summary: AFFILIATION_ELEMENT_PASSIVE_LINES.FIRE,
    detail:
      "Пассив даёт +14% к силе карты, если в бой выходит карта огня. Стихия хранителя и стихия карты — разные вещи: бонус только при совпадении.",
    pros: [
      "Стабильный процентный бонус в бою",
      "Хорошо с колодой карт огня",
      "Сильнее на картах белый–фиолетовый ранг",
    ],
    cons: [
      "Не работает с картами других стихий",
      "Слабее на оранжевых и красных картах",
    ],
    difficulty: "easy",
  },
  WATER: {
    summary: AFFILIATION_ELEMENT_PASSIVE_LINES.WATER,
    detail:
      "В бою +7% к силе. При поражении потеря рейтинга умножается на 0,75 (−25%). При ничьей (в т.ч. от уклонения) рейтинг не меняется.",
    pros: [
      "Страхует прогресс при поражениях",
      "Подходит для осторожной игры",
      "Множитель PTS только при LOSE",
    ],
    cons: [
      "Меньший боевой бонус, чем у огня/земли",
      "Не помогает выигрывать напрямую",
    ],
    difficulty: "easy",
  },
  EARTH: {
    summary: AFFILIATION_ELEMENT_PASSIVE_LINES.EARTH,
    detail:
      "На защитных картах пассив даёт +13% к силе. Лучше всего с танковым стилем и картами, которые получают бонус стиля в RPS.",
    pros: [
      "Сильный бонус на защитных картах",
      "Стабильный эффект без случайности",
      "Хорош для контр-игры",
    ],
    cons: [
      "Слабее с атакой и скоростью",
      "Нужно подбирать карты под стиль",
    ],
    difficulty: "medium",
  },
  LIGHTNING: {
    summary: AFFILIATION_ELEMENT_PASSIVE_LINES.LIGHTNING,
    detail:
      "Постоянно +4% к силе. Дополнительно есть шанс крита: ещё +18% к силе. В breakdown боя бонус может отображаться одной суммой с бейджем «Крит!».",
    pros: [
      "Может перевернуть близкий бой критом",
      "Всегда есть небольшой базовый бонус",
    ],
    cons: [
      "Крит зависит от удачи",
      "Без крита — один из слабейших пассивов",
    ],
    difficulty: "medium",
  },
  WIND: {
    summary: AFFILIATION_ELEMENT_PASSIVE_LINES.WIND,
    detail:
      "+5% к силе (+10% на карте SPEED). Если по силе ты проиграл, есть 12% шанс спасти поражение → ничья (рейтинг не падает). При победе или ничьей по силе уклонение не срабатывает.",
    pros: [
      "Сильнее на картах скорости",
      "Страхует от минуса рейтинга",
      "Работает только при реальном проигрыше",
    ],
    cons: [
      "Ничья не даёт победу и награды",
      "Уклонение зависит от удачи",
    ],
    difficulty: "hard",
  },
  ICE: {
    summary: AFFILIATION_ELEMENT_PASSIVE_LINES.ICE,
    detail:
      "+7% к силе и −3% к силе врага. Если у соперника сработал бонус стихии RPS, Лёд режет его на 50% (контр-пик стихии, не стиля).",
    pros: [
      "Свой бонус силы + ослабление врага",
      "Контрит соперников с преимуществом стихии",
    ],
    cons: [
      "Контр-пик бесполезен без бонуса стихии у соперника",
      "Нужно понимать RPS-матрицу",
    ],
    difficulty: "medium",
  },
}

/** @deprecated Используй AFFILIATION_ELEMENT_GUIDES[element].summary */
export const AFFILIATION_ELEMENT_SLOGANS: Record<CardElement, string> = {
  FIRE: AFFILIATION_ELEMENT_GUIDES.FIRE.summary,
  WATER: AFFILIATION_ELEMENT_GUIDES.WATER.summary,
  EARTH: AFFILIATION_ELEMENT_GUIDES.EARTH.summary,
  LIGHTNING: AFFILIATION_ELEMENT_GUIDES.LIGHTNING.summary,
  WIND: AFFILIATION_ELEMENT_GUIDES.WIND.summary,
  ICE: AFFILIATION_ELEMENT_GUIDES.ICE.summary,
}

export const AFFILIATION_PASSIVE_PHASE_HINT =
  "На картах белый–фиолетовый ранг пассив сильнее. На оранжевых и красных — слабее."

/**
 * Пассив для UI: приоритет у API (passport.affiliationPassive / mechanics),
 * локальные строки — только fallback.
 */
export function resolveAffiliationPassive(
  element: CardElement | null | undefined,
  fromApi?: AffiliationPassive | null
): AffiliationPassive | null {
  if (!element) return fromApi ?? null
  const apiTitle = fromApi?.title?.trim() ?? ""
  const apiDescription = fromApi?.description?.trim() ?? ""
  const fallbackTitle =
    AFFILIATION_ELEMENT_PASSIVE_TITLES[element] ||
    `Пассив · ${AFFILIATION_ELEMENT_TITLES[element]}`
  return {
    title: apiTitle || fallbackTitle,
    description: apiDescription || AFFILIATION_ELEMENT_PASSIVE_LINES[element],
  }
}

/** Собрать карту пассивов из GET /game/mechanics → affiliationElements */
export function affiliationPassivesFromMechanics(
  elements: ReadonlyArray<{ element: CardElement; title: string; description: string }>
): Partial<Record<CardElement, AffiliationPassive>> {
  const map: Partial<Record<CardElement, AffiliationPassive>> = {}
  for (const item of elements) {
    const title = item.title?.trim() ?? ""
    const description = item.description?.trim() ?? ""
    if (!title && !description) continue
    map[item.element] = {
      title: title || AFFILIATION_ELEMENT_PASSIVE_TITLES[item.element] || "",
      description:
        description || AFFILIATION_ELEMENT_PASSIVE_LINES[item.element] || "",
    }
  }
  return map
}

export const PVP_STATS_LEGACY_NOTE =
  "Статистика PvP считается с обновления игры"

export const AFFILIATION_ONE_TIME_WARNING =
  "Стихия хранителя выбирается один раз и не меняется, пока не начнёшь игру заново."

/** Градиент паспорта хранителя по стихии (тёмный → акцент) */
export const ELEMENT_PASSPORT_GRADIENTS: Record<
  CardElement,
  readonly [string, string, string]
> = {
  FIRE: ["#1a0a08", "#7f1d1d", "#c2410c"],
  WATER: ["#081018", "#1e3a5f", "#0369a1"],
  EARTH: ["#0a1208", "#3d2b1f", "#57534e"],
  LIGHTNING: ["#12100a", "#1a1a2e", "#ca8a04"],
  WIND: ["#081210", "#14532d", "#0891b2"],
  ICE: ["#081018", "#0c4a6e", "#0284c7"],
}

const DEFAULT_PASSPORT_GRADIENT = ["#0a0a14", "#12121f", "#1a1a2e"] as const

export function getPassportElementGradient(
  element: CardElement | null | undefined
): readonly [string, string, string] {
  if (!element) return DEFAULT_PASSPORT_GRADIENT
  return ELEMENT_PASSPORT_GRADIENTS[element]
}
